import { Injectable } from '@angular/core';
import {
  ALLOWED_LAUNCH_PARAMS,
  LAUNCH_PARAMS_STORAGE_KEY,
  LAUNCH_PARAMS_WARNING_KEY,
  LaunchOverrides,
} from '../models/dtr-types';

@Injectable({
  providedIn: 'root',
})
export class LaunchParamsService {
  // Assumes PathLocationStrategy — if hash routing is added, also parse location.hash
  resolveOverrides(search = window.location.search): LaunchOverrides {
    const params = new URLSearchParams(search);

    if (params.has('iss')) {
      this.validateParamNames(params);
      const overrides = this.extractOverrides(params);
      this.persistOverrides(overrides);
      return overrides;
    }

    return this.loadStoredOverrides();
  }

  // Logs any launch-phase warnings stored in sessionStorage, then clears them.
  displayAndClearLaunchWarnings(): void {
    try {
      const stored = sessionStorage.getItem(LAUNCH_PARAMS_WARNING_KEY);
      if (stored) {
        console.warn(stored);
        sessionStorage.removeItem(LAUNCH_PARAMS_WARNING_KEY);
      }
    } catch (err: unknown) {
      console.error('Something went wrong while retrieving Launch Params warnings', { err });
    }
  }

  private validateParamNames(params: URLSearchParams): void {
    const allowed = new Set<string>(ALLOWED_LAUNCH_PARAMS);
    const unknownParams = [...params.keys()].filter(k => !allowed.has(k));
    if (unknownParams.length > 0) {
      this.persistLaunchError(`Ignoring unrecognized URL parameter(s): '${unknownParams.join(', ')}'. Allowed parameters: ${ALLOWED_LAUNCH_PARAMS.join(', ')}`);
    }
  }

  private extractOverrides(params: URLSearchParams): LaunchOverrides {
    const overrides: LaunchOverrides = {};
    if (params.has('standalone')) overrides.standalone = params.get('standalone')!;
    if (params.has('useHook')) overrides.useHook = params.get('useHook')!;
    return overrides;
  }

  private persistOverrides(overrides: LaunchOverrides): void {
    if (Object.keys(overrides).length > 0) {
      sessionStorage.setItem(LAUNCH_PARAMS_STORAGE_KEY, JSON.stringify(overrides));
    } else {
      sessionStorage.removeItem(LAUNCH_PARAMS_STORAGE_KEY);
    }
  }

  private persistLaunchError(errorMessage: string): void {
    try {
      sessionStorage.setItem(LAUNCH_PARAMS_WARNING_KEY, errorMessage);
    } catch (err: unknown) {
      console.error('Something went wrong while persisting launch warnings', err);
    }
  }

  private loadStoredOverrides(): LaunchOverrides {
    const stored = sessionStorage.getItem(LAUNCH_PARAMS_STORAGE_KEY);
    if (!stored) return {};
    try {
      return JSON.parse(stored) as LaunchOverrides;
    } catch {
      return {};
    }
  }
}
